import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { featuredProjects } from "../../lib/content";

export default function FeaturedProjects() {
    return (
        <section className="relative py-24 md:py-32 border-t border-white/5" data-testid="featured-projects">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                    <div className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.2em] uppercase text-cyan-400 mb-6">
                            <span className="w-8 h-px bg-cyan-400/60" /> Featured Work
                        </div>
                        <h2 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter">
                            Systems that scale.<br />Infrastructure that endures.
                        </h2>
                    </div>
                    <Link
                        to="/projects"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-400 hover:text-cyan-300 transition-colors group whitespace-nowrap"
                    >
                        See full portfolio
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {featuredProjects.map((p, i) => (
                        <motion.article
                            key={p.title}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
                            className={`group relative overflow-hidden rounded-2xl border border-white/5 hover:border-blue-500/40 transition-all duration-500 ${
                                i % 3 === 0 ? "md:col-span-2 aspect-[21/9]" : "aspect-[4/3]"
                            }`}
                            data-testid={`project-card-${i}`}
                        >
                            <img
                                src={p.image}
                                alt={p.title}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />

                            <div className="relative h-full p-8 flex flex-col justify-end">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 backdrop-blur-md text-[10px] font-mono tracking-[0.2em] uppercase text-cyan-300">
                                        {p.sector}
                                    </span>
                                    <span className="text-xs font-mono text-slate-400">{p.year}</span>
                                </div>
                                <h3 className="font-heading font-black text-white text-2xl md:text-3xl leading-tight tracking-tight mb-3">
                                    {p.title}
                                </h3>
                                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-slate-400">
                                    <span className="inline-flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3 text-cyan-400" /> {p.location}
                                    </span>
                                    <span className="font-mono text-slate-500">{p.scope}</span>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
}
